/**
 * CurrencyFxCard — admin editor for the currency ladder that powers
 * every shopper-facing price on the storefront.
 *
 * Data source: `/api/admin/currency/config` (GET/PUT). Behaviour:
 *   • Table row per currency with editable code, symbol, name, rate,
 *     locale and flag emoji.
 *   • Base currency is always locked to rate = 1.0 (the storefront
 *     stores prices in INR).
 *   • Add / remove rows inline; base currency row cannot be removed.
 *   • Save button PUTs the whole ladder atomically. On success, all
 *     shopper surfaces re-render with the new rates on their next
 *     React-Query refetch.
 *   • Reset button restores the shipped defaults.
 *
 * "Rate" semantics: display_currency_per_1_INR. So `USD.rate = 0.01205`
 * means ₹1 == $0.01205. Displaying "1 INR = 0.01205 USD" makes the
 * relationship obvious to non-technical admins.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save, RotateCcw, Trash2, Plus, Coins, AlertTriangle, RefreshCw, CheckCircle2, CircleX } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";

interface CurrencyRow {
  code: string;
  symbol: string;
  name: string;
  rate: number;
  locale: string;
  flag?: string | null;
}

interface CurrencyConfig {
  base: string;
  note?: string | null;
  currencies: CurrencyRow[];
  updated_at?: string | null;
  updated_by?: string | null;
  auto_refresh_enabled?: boolean;
  last_auto_refresh_at?: string | null;
  last_auto_refresh_ok?: boolean | null;
  last_auto_refresh_detail?: string | null;
  last_auto_refresh_provider?: string | null;
  last_auto_refresh_updated?: string[];
}

const EMPTY_ROW: CurrencyRow = { code: "", symbol: "", name: "", rate: 0, locale: "en-US", flag: "" };

export const CurrencyFxCard: React.FC = () => {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const readonly = (me?.role || "user") !== "admin";

  const q = useQuery<CurrencyConfig>({
    queryKey: ["admin-currency-config"],
    queryFn: async () => (await api.get("/admin/currency/config")).data,
    staleTime: 60_000,
  });

  const [rows, setRows] = useState<CurrencyRow[]>([]);
  const [base, setBase] = useState<string>("INR");
  const [note, setNote] = useState<string>("");

  // Hydrate local draft from the server response the first time it arrives
  // and any time the admin resets.
  useEffect(() => {
    if (q.data) {
      setRows(q.data.currencies.map((c) => ({ ...c })));
      setBase(q.data.base || "INR");
      setNote(q.data.note || "");
    }
  }, [q.data]);

  const save = useMutation({
    mutationFn: async () =>
      (await api.put("/admin/currency/config", {
        base,
        note: note.trim() || undefined,
        currencies: rows.map((r) => ({
          code: r.code.trim().toUpperCase(),
          symbol: r.symbol.trim() || r.code.trim().toUpperCase(),
          name: r.name.trim() || r.code.trim().toUpperCase(),
          rate: r.code.trim().toUpperCase() === base ? 1.0 : Number(r.rate),
          locale: r.locale.trim() || "en-US",
          flag: r.flag?.trim() || undefined,
        })),
      })).data as CurrencyConfig,
    onSuccess: (d) => {
      toast.success(`FX ladder saved (${d.currencies.length} currencies).`);
      qc.invalidateQueries({ queryKey: ["admin-currency-config"] });
      // Also invalidate the public config so shopper surfaces pick up the
      // new ladder without a hard reload.
      qc.invalidateQueries({ queryKey: ["currency-config"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const resetToDefaults = useMutation({
    mutationFn: async () => (await api.post("/admin/currency/config/reset")).data,
    onSuccess: () => {
      toast.success("Restored the default currency ladder.");
      qc.invalidateQueries({ queryKey: ["admin-currency-config"] });
      qc.invalidateQueries({ queryKey: ["currency-config"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Reset failed"),
  });

  const toggleAutoRefresh = useMutation({
    mutationFn: async (enabled: boolean) =>
      (await api.put("/admin/currency/auto-refresh", { enabled })).data as CurrencyConfig,
    onSuccess: (d) => {
      toast.success(d.auto_refresh_enabled ? "Daily FX refresh turned on." : "Daily FX refresh turned off.");
      qc.invalidateQueries({ queryKey: ["admin-currency-config"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Toggle failed"),
  });

  const refreshNow = useMutation({
    mutationFn: async () => (await api.post("/admin/currency/refresh")).data as CurrencyConfig,
    onSuccess: (d) => {
      if (d.last_auto_refresh_ok) {
        const count = d.last_auto_refresh_updated?.length || 0;
        toast.success(count
          ? `Live rates refreshed — ${count} code${count === 1 ? "" : "s"} updated.`
          : "Live rates already up to date.");
      } else {
        toast.error(d.last_auto_refresh_detail || "Refresh failed — provider unreachable.");
      }
      qc.invalidateQueries({ queryKey: ["admin-currency-config"] });
      qc.invalidateQueries({ queryKey: ["currency-config"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Refresh failed"),
  });

  const patchRow = (i: number, patch: Partial<CurrencyRow>) => {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };
  const removeRow = (i: number) => {
    const code = rows[i]?.code?.trim().toUpperCase();
    if (code === base.toUpperCase()) {
      toast.error("Can't remove the base currency.");
      return;
    }
    setRows((prev) => prev.filter((_, idx) => idx !== i));
  };
  const addRow = () => setRows((prev) => [...prev, { ...EMPTY_ROW }]);

  // Detect duplicate codes so the UI can flag them before the API rejects.
  const dupCodes = useMemo(() => {
    const seen: Record<string, number> = {};
    for (const r of rows) {
      const c = (r.code || "").trim().toUpperCase();
      if (!c) continue;
      seen[c] = (seen[c] || 0) + 1;
    }
    return Object.keys(seen).filter((k) => seen[k] > 1);
  }, [rows]);

  const baseInLadder = rows.some((r) => (r.code || "").trim().toUpperCase() === base.toUpperCase());
  const anyBlank = rows.some(
    (r) => !(r.code || "").trim() || !(r.symbol || "").trim() || !(r.name || "").trim(),
  );
  const anyBadRate = rows.some((r) => {
    const c = (r.code || "").trim().toUpperCase();
    if (c === base.toUpperCase()) return false;
    const n = Number(r.rate);
    return !Number.isFinite(n) || n <= 0;
  });
  const canSave = !readonly && rows.length > 0 && dupCodes.length === 0 && baseInLadder && !anyBlank && !anyBadRate;

  return (
    <div className="sg-card p-6 mt-6" data-testid="currency-fx-card">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="sg-label mb-1 flex items-center gap-1.5">
            <Coins className="h-3.5 w-3.5" /> FX Editor
          </div>
          <div className="font-display text-lg font-semibold tracking-tight">
            Display currency ladder
          </div>
          <p className="text-sm text-[color:var(--sg-muted)] mt-1 leading-relaxed max-w-3xl">
            Rates control how ₹ prices display to global shoppers. Format is
            <span className="font-mono text-[11px] mx-1 px-1.5 py-0.5 rounded bg-slate-100">
              display_units_per_1_INR
            </span>
            — so <span className="font-mono text-[11px]">USD.rate = 0.01205</span> means <b>₹1 = $0.01205</b>.
            The base currency ({base}) always stays at rate 1. Checkout still bills in {base}.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {q.data?.updated_at && (
            <div className="text-[11px] text-[color:var(--sg-muted)]" data-testid="currency-fx-updated-at">
              Last saved {new Date(q.data.updated_at).toLocaleString()}
            </div>
          )}
          {!readonly && (
            <Button
              variant="outline"
              size="sm"
              data-testid="currency-fx-reset"
              disabled={resetToDefaults.isPending}
              onClick={() => {
                if (window.confirm("Restore the default currency ladder? All edits will be lost.")) {
                  resetToDefaults.mutate();
                }
              }}
              className="h-8"
            >
              {resetToDefaults.isPending
                ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                : <RotateCcw className="h-3.5 w-3.5 mr-1" />}
              Reset to defaults
            </Button>
          )}
        </div>
      </div>

      {dupCodes.length > 0 && (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="currency-fx-dupes">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />
          Duplicate codes: <b>{dupCodes.join(", ")}</b>. Each currency must be unique.
        </div>
      )}
      {!baseInLadder && (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="currency-fx-missing-base">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />
          Base currency <b>{base}</b> must appear in the ladder below.
        </div>
      )}

      {/* Live FX refresh — daily cron toggle + on-demand button. */}
      <div
        className="mb-4 rounded-lg border border-[color:var(--sg-border)] bg-[color:var(--sg-muted-bg)]/40 p-3 flex flex-wrap items-center gap-3"
        data-testid="currency-fx-live-panel"
      >
        <div className="flex items-center gap-3 flex-1 min-w-[240px]">
          <div className={`h-8 w-8 rounded-full grid place-items-center ${
            q.data?.auto_refresh_enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
          }`}>
            <RefreshCw className={`h-4 w-4 ${refreshNow.isPending ? "animate-spin" : ""}`} strokeWidth={2} />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-[color:var(--sg-fg)] leading-tight">
              Live FX refresh
            </div>
            <div className="text-[11px] text-[color:var(--sg-muted)] leading-snug">
              Pulls daily rates from{" "}
              <a className="underline hover:text-[color:var(--sg-fg)]" href="https://www.exchangerate-api.com/" target="_blank" rel="noreferrer noopener">
                exchangerate-api.com
              </a>
              {" "}(free, no key). Runs at 03:15 UTC · only updates existing rows.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {q.data?.last_auto_refresh_at && (
            <div
              className="text-[11px] flex items-center gap-1"
              data-testid="currency-fx-last-refresh"
              title={q.data.last_auto_refresh_detail || ""}
            >
              {q.data.last_auto_refresh_ok
                ? <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                : <CircleX className="h-3 w-3 text-rose-500" />}
              <span className="text-[color:var(--sg-muted)]">
                {q.data.last_auto_refresh_ok ? "Last synced" : "Last try"} {new Date(q.data.last_auto_refresh_at).toLocaleString()}
              </span>
            </div>
          )}
          {!readonly && (
            <Button
              variant="outline"
              size="sm"
              data-testid="currency-fx-refresh-now"
              disabled={refreshNow.isPending}
              onClick={() => refreshNow.mutate()}
              className="h-8"
            >
              {refreshNow.isPending
                ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
              Refresh now
            </Button>
          )}
          <label className="flex items-center gap-2 cursor-pointer" data-testid="currency-fx-auto-refresh-toggle">
            <Switch
              checked={!!q.data?.auto_refresh_enabled}
              disabled={readonly || toggleAutoRefresh.isPending}
              onCheckedChange={(v) => toggleAutoRefresh.mutate(!!v)}
            />
            <span className="text-xs font-medium text-[color:var(--sg-fg)]">Daily auto-refresh</span>
          </label>
        </div>
      </div>

      {q.isLoading ? (
        <div className="sg-skeleton h-40" />
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs" data-testid="currency-fx-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[color:var(--sg-muted)]">
                <th className="text-left px-2 py-2 w-16">Flag</th>
                <th className="text-left px-2 py-2 w-16">Code</th>
                <th className="text-left px-2 py-2 w-14">Symbol</th>
                <th className="text-left px-2 py-2">Name</th>
                <th className="text-right px-2 py-2 w-40">Rate per 1 {base}</th>
                <th className="text-left px-2 py-2 w-28">Locale</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--sg-border)]">
              {rows.map((r, i) => {
                const code = (r.code || "").trim().toUpperCase();
                const isBase = code === base.toUpperCase();
                const isDup = code && dupCodes.includes(code);
                return (
                  <tr key={i} data-testid={`currency-fx-row-${i}`} className={isDup ? "bg-amber-50" : undefined}>
                    <td className="px-2 py-2">
                      <input
                        data-testid={`currency-fx-flag-${i}`}
                        value={r.flag || ""}
                        onChange={(e) => patchRow(i, { flag: e.target.value })}
                        disabled={readonly}
                        placeholder="🇮🇳"
                        className="w-full h-8 px-2 rounded border border-[color:var(--sg-border)] bg-transparent text-center"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        data-testid={`currency-fx-code-${i}`}
                        value={r.code}
                        onChange={(e) => patchRow(i, { code: e.target.value.toUpperCase().slice(0, 4) })}
                        disabled={readonly || isBase}
                        placeholder="USD"
                        className={`w-full h-8 px-2 rounded border bg-transparent font-mono uppercase tracking-wider ${
                          isDup ? "border-amber-400" : "border-[color:var(--sg-border)]"
                        } ${isBase ? "opacity-70" : ""}`}
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        data-testid={`currency-fx-symbol-${i}`}
                        value={r.symbol}
                        onChange={(e) => patchRow(i, { symbol: e.target.value.slice(0, 8) })}
                        disabled={readonly}
                        placeholder="$"
                        className="w-full h-8 px-2 rounded border border-[color:var(--sg-border)] bg-transparent"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        data-testid={`currency-fx-name-${i}`}
                        value={r.name}
                        onChange={(e) => patchRow(i, { name: e.target.value.slice(0, 40) })}
                        disabled={readonly}
                        placeholder="US Dollar"
                        className="w-full h-8 px-2 rounded border border-[color:var(--sg-border)] bg-transparent"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex items-center justify-end gap-2">
                        <span className="text-[10px] text-[color:var(--sg-muted)] tabular-nums">1 {base} =</span>
                        <input
                          data-testid={`currency-fx-rate-${i}`}
                          type="number"
                          step="0.00001"
                          min={0}
                          value={isBase ? 1 : r.rate}
                          onChange={(e) => patchRow(i, { rate: parseFloat(e.target.value) })}
                          disabled={readonly || isBase}
                          className={`w-24 h-8 px-2 rounded border bg-transparent text-right font-mono tabular-nums ${
                            !isBase && (!Number.isFinite(Number(r.rate)) || Number(r.rate) <= 0)
                              ? "border-amber-400"
                              : "border-[color:var(--sg-border)]"
                          } ${isBase ? "opacity-70" : ""}`}
                        />
                        <span className="text-[10px] font-mono uppercase text-[color:var(--sg-muted)]">
                          {code || "—"}
                        </span>
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <input
                        data-testid={`currency-fx-locale-${i}`}
                        value={r.locale}
                        onChange={(e) => patchRow(i, { locale: e.target.value.slice(0, 16) })}
                        disabled={readonly}
                        placeholder="en-US"
                        className="w-full h-8 px-2 rounded border border-[color:var(--sg-border)] bg-transparent font-mono"
                      />
                    </td>
                    <td className="px-2 py-2 text-right">
                      {!readonly && !isBase ? (
                        <button
                          type="button"
                          onClick={() => removeRow(i)}
                          data-testid={`currency-fx-remove-${i}`}
                          className="h-8 w-8 grid place-items-center text-slate-400 hover:text-rose-500"
                          title={`Remove ${code || "row"}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      ) : (
                        <span className="text-[9px] uppercase tracking-widest text-[color:var(--sg-muted)]">
                          {isBase ? "base" : ""}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!readonly && (
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={addRow}
            data-testid="currency-fx-add-row"
            className="h-8"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add currency
          </Button>
          <Button
            data-testid="currency-fx-save"
            onClick={() => save.mutate()}
            disabled={!canSave || save.isPending}
            className="bg-[color:var(--sg-fg)] text-white hover:bg-black hover:text-white h-9"
          >
            {save.isPending
              ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              : <Save className="h-4 w-4 mr-1" />}
            Save FX ladder
          </Button>
        </div>
      )}
    </div>
  );
};
